/* =========================================================
   EGODRIFT Variant State
   Version: 1.0.1 — 2026-07-19
   Last change: add missing version header (audit item 5)
========================================================= */
(() => {
  const dataEl = document.querySelector('[data-ego-var-data]');

  if (!dataEl) return;

  let variantsById = {};
  let variants = [];
  let currentVariantId = null;
  let isProgrammaticUpdate = false;

  if ('scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
  }

  function preserveScroll() {
    return {
      x: window.scrollX,
      y: window.scrollY
    };
  }

  function restoreScroll(position) {
    if (!position) return;

    requestAnimationFrame(() => {
      window.scrollTo(position.x, position.y);
    });

    setTimeout(() => {
      window.scrollTo(position.x, position.y);
    }, 50);

    setTimeout(() => {
      window.scrollTo(position.x, position.y);
    }, 150);
  }

  try {
    variantsById = JSON.parse(dataEl.textContent);
    variants = Object.values(variantsById);
  } catch (error) {
    console.warn('[EGO VAR STATE] Failed to parse variant data:', error);
    return;
  }

  function normalize(value) {
    return (value || '').toString().trim();
  }

  function getSelectedOptions() {
    const selectedOptions = [];
    const picker = document.querySelector('variant-selects, variant-radios');

    if (picker) {
      picker.querySelectorAll('fieldset').forEach(fieldset => {
        const checked = fieldset.querySelector('input[type="radio"]:checked');
        if (checked) selectedOptions.push(checked.value);
      });

      picker.querySelectorAll('select').forEach(select => {
        if (select.value) selectedOptions.push(select.value);
      });

      if (selectedOptions.length) return selectedOptions;
    }

    return selectedOptions;
  }

  function getVariantFromOptions(options) {
    if (!options || !options.length) return null;

    return variants.find(variant => {
      if (!variant.options) return false;
      if (variant.options.length !== options.length) return false;

      return variant.options.every((option, index) => {
        return normalize(option) === normalize(options[index]);
      });
    }) || null;
  }

  function getAvailableVariantFromOptions(options) {
    const exactVariant = getVariantFromOptions(options);

    if (exactVariant && exactVariant.available) {
      return exactVariant;
    }

    return null;
  }

  function getVariantFromConfiguration(configuration) {
    const normalizedConfiguration = normalize(configuration);

    if (!normalizedConfiguration) return null;

    return variants.find(variant => {
      return normalize(variant.configuration) === normalizedConfiguration;
    }) || null;
  }

  function findBestAvailableVariant(selectedOptions) {
    if (!selectedOptions || !selectedOptions.length) {
      return variants.find(variant => variant.available) || variants[0] || null;
    }

    for (let preservedCount = selectedOptions.length; preservedCount >= 1; preservedCount--) {
      const preservedOptions = selectedOptions.slice(0, preservedCount);

      const matchingVariants = variants.filter(variant => {
        if (!variant.available || !variant.options) return false;

        return preservedOptions.every((optionValue, index) => {
          return normalize(variant.options[index]) === normalize(optionValue);
        });
      });

      if (matchingVariants.length) {
        return matchingVariants[0];
      }
    }

    return variants.find(variant => variant.available) || variants[0] || null;
  }

  function findBestVariantFromConfigurationParts(configurationParts) {
    if (!configurationParts || !configurationParts.length) {
      return variants.find(variant => variant.available) || variants[0] || null;
    }

    for (let preservedCount = configurationParts.length; preservedCount >= 1; preservedCount--) {
      const preservedSlug = configurationParts.slice(0, preservedCount).join('-');

      const matchingVariants = variants.filter(variant => {
        if (!variant.available || !variant.configuration) return false;

        return normalize(variant.configuration).startsWith(normalize(preservedSlug));
      });

      if (matchingVariants.length) {
        return matchingVariants[0];
      }
    }

    return variants.find(variant => variant.available) || variants[0] || null;
  }

  function setVariantFormInput(variant) {
    if (!variant || !variant.id) return;

    document
      .querySelectorAll('form[action*="/cart/add"] input[name="id"]')
      .forEach(input => {
        input.value = variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
  }

  function emitVariantChange(variant, force = false, meta = {}) {
    if (!variant || !variant.id) return;

    if (!force && String(variant.id) === String(currentVariantId)) return;

    currentVariantId = String(variant.id);

    document.dispatchEvent(
      new CustomEvent('ego:variant-change', {
        detail: {
          variant: variant,
          variantId: variant.id,
          configuration: variant.configuration || null,
          autoCorrected: meta.autoCorrected || false,
          originallySelectedOptions: meta.originallySelectedOptions || null
        }
      })
    );

  }

  function selectVariantOptions(variant, meta = {}) {
    if (!variant || !variant.options) return;

    isProgrammaticUpdate = true;

    const scrollPosition = preserveScroll();
    const picker = document.querySelector('variant-selects, variant-radios');

    if (!picker) {
      setVariantFormInput(variant);
      emitVariantChange(variant, true, meta);
      restoreScroll(scrollPosition);
      isProgrammaticUpdate = false;
      return;
    }

    const fieldsets = picker.querySelectorAll('fieldset');
    const selects = picker.querySelectorAll('select');

    variant.options.forEach((optionValue, index) => {
      const fieldset = fieldsets[index];

      if (fieldset) {
        const matchingRadio = Array.from(fieldset.querySelectorAll('input[type="radio"]')).find(radio => {
          return normalize(radio.value) === normalize(optionValue);
        });

        if (matchingRadio) {
          matchingRadio.checked = true;
          matchingRadio.dispatchEvent(new Event('input', { bubbles: true }));
          matchingRadio.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      const select = selects[index];

      if (select) {
        select.value = optionValue;
        select.dispatchEvent(new Event('input', { bubbles: true }));
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    setVariantFormInput(variant);

    setTimeout(() => {
      setVariantFormInput(variant);
      emitVariantChange(variant, true, meta);
      restoreScroll(scrollPosition);
      isProgrammaticUpdate = false;
    }, 250);
  }

  function updateVariantState(force = false) {
    const selectedOptions = getSelectedOptions();

    if (!selectedOptions.length) return;

    let variant = getAvailableVariantFromOptions(selectedOptions);

    if (!variant) {
      variant = findBestAvailableVariant(selectedOptions);

      if (variant) {
        selectVariantOptions(variant, {
          autoCorrected: true,
          originallySelectedOptions: selectedOptions
        });
      }

      return;
    }

    emitVariantChange(variant, force);
  }

  function applyConfigurationFromUrl() {
    const url = new URL(window.location.href);
    const configuration = url.searchParams.get('configuration');

    if (!configuration) return false;

    const exactVariant = getVariantFromConfiguration(configuration);

    if (exactVariant) {
      selectVariantOptions(exactVariant);
      emitVariantChange(exactVariant, true);
      return true;
    }

    const configurationParts = configuration
      .split('-')
      .map(part => part.trim())
      .filter(Boolean);

    if (!configurationParts.length) return false;

    const fallbackVariant = findBestVariantFromConfigurationParts(configurationParts);

    if (!fallbackVariant) {
      console.warn('[EGO VAR STATE] No fallback variant found for configuration:', configuration);
      return false;
    }

    selectVariantOptions(fallbackVariant, {
      autoCorrected: true,
      originallySelectedOptions: configurationParts
    });

    return true;
  }

  const noticeEl = document.querySelector('.ego-var-notice');

  document.addEventListener('ego:variant-change', event => {
    if (!noticeEl) return;

    const detail = event.detail;

    if (!detail.autoCorrected) {
      noticeEl.hidden = true;
      noticeEl.innerHTML = '';
      return;
    }

   const variantTitle =
  detail.variant?.displayTitle ||
  detail.variant?.name ||
  detail.variant?.title ||
  '';

    noticeEl.innerHTML = `
      <strong>Please note:</strong>
      Your selection was adjusted to the nearest available configuration:<br />
      <strong>${variantTitle}</strong>
    `;

    noticeEl.hidden = false;
  });

  function scheduleUpdate() {
    if (isProgrammaticUpdate) return;

    const scrollPosition = preserveScroll();

    setTimeout(() => {
      updateVariantState(false);
      restoreScroll(scrollPosition);
    }, 0);

    setTimeout(() => {
      updateVariantState(false);
      restoreScroll(scrollPosition);
    }, 100);

    setTimeout(() => {
      updateVariantState(false);
      restoreScroll(scrollPosition);
    }, 300);
  }

  document.addEventListener('change', event => {
    if (
      event.target.closest('variant-selects') ||
      event.target.closest('variant-radios')
    ) {
      scheduleUpdate();
    }
  });

  document.addEventListener('click', event => {
    if (
      event.target.closest('variant-selects') ||
      event.target.closest('variant-radios') ||
      event.target.closest('.product-form__input')
    ) {
      scheduleUpdate();
    }
  });

  const originalPushState = history.pushState;
  const originalReplaceState = history.replaceState;

  history.pushState = function () {
    originalPushState.apply(this, arguments);
    scheduleUpdate();
  };

  history.replaceState = function () {
    originalReplaceState.apply(this, arguments);
    scheduleUpdate();
  };

  window.addEventListener('popstate', scheduleUpdate);

  if (!applyConfigurationFromUrl()) {
    updateVariantState(true);
  }
})();